import { z } from "zod";

/**
 * Canonical in-memory money: nonneg `bigint` in the smallest coin unit,
 * branded so a raw `bigint` can't be passed where `MoneyAmount` is required
 * without parsing. Never use `number` for money — JS floats silently lose
 * precision past 2^53.
 */
/**
 * Upper bound for any money amount: the Postgres `bigint`/int8 max the ledger,
 * wallet, and approval columns are stored in. Capping here turns an over-large
 * amount into a clean 400 at the request boundary instead of a write-time 500
 * (`value out of range for type bigint`), while never rejecting a value the DB
 * could legitimately hold (a balance read back is by definition ≤ this).
 */
export const MAX_MONEY_AMOUNT = 9223372036854775807n;

export const MoneyAmount = z
  .bigint()
  .nonnegative()
  .max(MAX_MONEY_AMOUNT, "money exceeds the maximum supported amount")
  .brand<"MoneyAmount">();
export type MoneyAmount = z.output<typeof MoneyAmount>;

const INTEGER_STRING = /^(0|[1-9]\d*)$/;

/**
 * Wire-form parser for money. JSON can't carry `bigint`, so the edge sends a
 * positive-integer string; this schema accepts that OR a raw `bigint`, then
 * pipes into `MoneyAmount`. Rejects negatives, leading zeros, and decimals.
 */
export const MoneyAmountInput = z
  .union([
    z.bigint(),
    z.string().regex(INTEGER_STRING, "money must be a positive integer string in smallest units"),
  ])
  .transform((v, ctx) => {
    try {
      return typeof v === "bigint" ? v : BigInt(v);
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "invalid bigint" });
      return z.NEVER;
    }
  })
  .pipe(MoneyAmount);
export type MoneyAmountInput = z.input<typeof MoneyAmountInput>;

/**
 * Signed delta for ledger math (reversals, net positions). Never persisted —
 * the ledger stores `direction` + nonneg amount; this is only for in-memory
 * arithmetic.
 */
export const MoneySignedDelta = z.bigint().brand<"MoneySignedDelta">();
export type MoneySignedDelta = z.output<typeof MoneySignedDelta>;
